/**
 * Contractor Directory Page
 *
 * Public directory of all published brand kits.
 * URL: /contractors (marketing site: rooflyzer.pro/contractors)
 * Non-indexable for now (noindex, nofollow).
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAllBrandKits, searchBrandKits, type BrandKitListItem } from '@/lib/brandKitApi';
import { getAppLink } from '@/lib/links';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, Building2, MapPin, ArrowRight } from 'lucide-react';
import { SEOHead } from '@/components/seo/SEOHead';

const ContractorDirectoryPage: React.FC = () => {
  const [brandKits, setBrandKits] = useState<BrandKitListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredKits, setFilteredKits] = useState<BrandKitListItem[]>([]);

  useEffect(() => {
    const loadBrandKits = async () => {
      try {
        setLoading(true);
        const data = await getAllBrandKits();
        setBrandKits(data);
        setFilteredKits(data);
      } catch (error) {
        console.error('Error loading brand kits:', error);
      } finally {
        setLoading(false);
      }
    };

    loadBrandKits();
  }, []);

  useEffect(() => {
    const performSearch = async () => {
      if (!searchQuery.trim()) {
        setFilteredKits(brandKits);
        return;
      }

      try {
        const results = await searchBrandKits(searchQuery);
        setFilteredKits(results);
      } catch (error) {
        console.error('Error searching brand kits:', error);
        // Fallback to client-side filtering
        const query = searchQuery.toLowerCase();
        const filtered = brandKits.filter(
          (kit) =>
            kit.company_name?.toLowerCase().includes(query) ||
            kit.city?.toLowerCase().includes(query) ||
            kit.state?.toLowerCase().includes(query) ||
            kit.slug?.toLowerCase().includes(query)
        );
        setFilteredKits(filtered);
      }
    };

    const timeoutId = setTimeout(performSearch, 300);
    return () => clearTimeout(timeoutId);
  }, [searchQuery, brandKits]);

  return (
    <>
      <SEOHead
        title="Roofing Companies Directory | Rooflyzer"
        description="Browse our directory of professional roofing contractors. View company profiles, services, and contact information."
        canonicalUrl="/contractors"
        robots="noindex, nofollow"
      />

      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero Section */}
        <div className="bg-gray-900 text-white py-16">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Trusted Roofing Contractors</h1>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto mb-8">
              Browse our directory of professional roofing contractors. View company profiles,
              services, and contact information.
            </p>

            {/* Search Bar */}
            <div className="max-w-lg mx-auto relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 size-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by company name, city, or state..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-6 text-lg bg-white text-gray-900 border-0 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-4 py-12">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-8 animate-spin text-gray-400" />
            </div>
          ) : filteredKits.length === 0 ? (
            <div className="text-center py-20">
              <Building2 className="h-16 w-16 text-gray-300 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-700 mb-2">No Companies Found</h2>
              <p className="text-gray-500">
                {searchQuery ? 'Try a different search term.' : 'No published contractors yet.'}
              </p>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-2xl font-bold text-gray-900">
                  {searchQuery ? `Results for "${searchQuery}"` : 'All Contractors'}
                </h2>
                <Badge variant="secondary" className="text-sm">
                  {filteredKits.length} {filteredKits.length === 1 ? 'Contractor' : 'Contractors'}
                </Badge>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredKits.map((kit) => (
                  <Link key={kit.id} to={`/contractors/${kit.slug}`} className="group">
                    <div className="h-full hover:shadow-lg transition-shadow duration-200 border-2 border-transparent hover:border-blue-200 rounded-lg bg-white p-6">
                      <div className="flex items-start gap-4">
                        {kit.logo_url ? (
                          <img
                            src={kit.logo_url}
                            alt={kit.company_name}
                            className="h-16 w-16 object-contain rounded-lg bg-gray-100 p-2"
                          />
                        ) : (
                          <div
                            className="h-16 w-16 rounded-lg flex items-center justify-center text-white font-bold text-2xl"
                            style={{ backgroundColor: kit.primary_color || '#3b82f6' }}
                          >
                            {kit.company_name?.charAt(0) || 'R'}
                          </div>
                        )}

                        <div className="flex-1 min-w-0">
                          <h3 className="font-bold text-lg text-gray-900 group-hover:text-blue-600 transition-colors truncate">
                            {kit.company_name || 'Unnamed Company'}
                          </h3>

                          {(kit.city || kit.state) && (
                            <p className="text-sm text-gray-500 flex items-center gap-1 mt-1">
                              <MapPin className="size-3.5" />
                              {[kit.city, kit.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Color Preview */}
                      {kit.primary_color && (
                        <div className="flex gap-1.5 mt-4">
                          <div
                            className="h-6 flex-1 rounded"
                            style={{ backgroundColor: kit.primary_color }}
                            title="Primary Color"
                          />
                        </div>
                      )}

                      <div className="flex items-center justify-end mt-4 text-sm text-blue-600 group-hover:text-blue-700">
                        View Profile
                        <ArrowRight className="size-4 ml-1 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Footer CTA */}
        <footer className="border-t border-gray-200 mt-12 py-8">
          <div className="container mx-auto px-4 text-center text-gray-500 text-sm">
            <p>&copy; {new Date().getFullYear()} Rooflyzer. All rights reserved.</p>
            <p className="mt-2">
              Are you a roofing contractor?
              <a href={getAppLink('/register')} className="text-blue-600 hover:underline ml-1">
                Join our directory
              </a>
            </p>
          </div>
        </footer>
      </div>
    </>
  );
};

export { ContractorDirectoryPage };
